import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { ButtonPrimary, ButtonSecondary } from 'shared/components/Button';
import FormError from 'shared/components/FormError';
import { Input } from 'shared/components/Input';
import { Divider, Spacer } from 'shared/components/Layout';
import { H4, P } from 'shared/components/Typography';
import { Wrapper } from 'shared/components/Wrapper';
import {
  anzac,
  yellowLight,
  silverChaliceTwo,
  mischka,
  athensGray,
} from 'shared/config/Colors';
import { RootState } from 'store';
import styled from 'styled-components';

import { onlyNumberWithThreeDecimalsValidation } from 'shared/validations/validations';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { OrderStatusEnums } from 'order/enums/orderEnums';
import { setMultiplier, UpdateMultiplier } from 'order/store/orderActions';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';
import { useParams } from 'react-router';
import { OrderPageParams } from 'order/interfaces/OrderPageParams';
import { ProductLineEnums } from 'order/enums/ProductLineEnums';

const Container = styled(Wrapper)<{
  backgroundColor: string;
  borderColor: string;
}>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  border: 2px solid ${({ borderColor }) => borderColor};
  border-radius: 16px;
  padding: 24px 32px;
  margin-left: 16px;
  width: 515px;
`;

const StyledInput = styled(Input)`
  padding: 5px !important;
`;

const HeaderWraper = styled(Wrapper)`
  padding: 0px 5px 0px 8px;
  width: 192px;
`;

export const Multiplier = ({ onUpdate }: { onUpdate: () => void }) => {
  const { styles, canEdit, priceReview, order } = useSelector(
    (state: RootState) => state.orderReducer
  );
  const dispatch = useAppDispatch();

  const { orderId } = useParams<OrderPageParams>();

  const [isLoading, setIsLodaing] = useState(false);

  const methods = useForm<{
    multiplier: {
      current: number | string;
      estimator: number | string;
      name: string;
    }[];
  }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      multiplier: [],
    },
  });

  const { watch } = methods;
  const multiplierWatcher = watch('multiplier');

  const mappedStyles = styles
    ?.filter(
      (item) => item.productLine.name !== ProductLineEnums.PRODUCT_LINE_INOVAE2O
    )
    .map((style) => {
      return {
        current: priceReview?.multipliers?.current.find(
          (item) => item.productLine === style.productLine.name
        )?.value,
        estimator: priceReview?.multipliers?.estimator.find(
          (item) => item.productLine === style.productLine.name
        )?.value,
        index: priceReview?.multipliers?.current.findIndex(
          (item) => item.productLine === style.productLine.name
        ),
        ...style.productLine,
      };
    });

  const productLines = Array.from(
    new Map(mappedStyles?.map((item) => [item.id, item])).values()
  );

  const getColorsByStatus = useMemo(
    () =>
      canEdit &&
      order &&
      order?.status?.id === OrderStatusEnums['Not Submitted'] &&
      multiplierWatcher.some(
        (item) => Number(item.current) !== Number(item.estimator)
      )
        ? { borderColor: anzac, backgroundColor: yellowLight }
        : { borderColor: mischka, backgroundColor: athensGray },
    [order, multiplierWatcher]
  );

  const isDisabled =
    order &&
    order?.status?.id === OrderStatusEnums['Not Submitted'] &&
    canEdit &&
    !isLoading;

  const isInputValid = (index: number) => {
    if (methods.formState.errors.multiplier) {
      return methods.formState.errors.multiplier[index] ? 'true' : 'false';
    }
    return 'false';
  };

  const onSave = (data: {
    multiplier: {
      current: number | string;
      estimator: number | string;
      name: string;
    }[];
    resetToDefault: boolean;
  }) => {
    setIsLodaing(true);
    const mapUpdateMultiplier: UpdateMultiplier = {
      multiplierEstimatorCustom: Number(
        data.multiplier.find(
          (item) => item.name === ProductLineEnums.PRODUCT_LINE_CUSTOM
        )!.estimator
      ),
      multiplierEstimatorInovae: Number(
        data.multiplier.find(
          (item) => item.name === ProductLineEnums.PRODUCT_LINE_INOVAE
        )!.estimator
      ),
      multiplierEstimatorPrelude: Number(
        data.multiplier.find(
          (item) => item.name === ProductLineEnums.PRODUCT_LINE_PRELUDE
        )!.estimator
      ),
      multiplierEstimatorRevolae: Number(
        data.multiplier.find(
          (item) => item.name === ProductLineEnums.PRODUCT_LINE_REVOLAE
        )!.estimator
      ),
      multiplierEstimatorCurate: Number(
        data.multiplier.find(
          (item) => item.name === ProductLineEnums.PRODUCT_LINE_ICS
        )!.estimator
      ),
      resetToDefault: data.resetToDefault,
    };

    dispatch(
      setMultiplier(
        { data: mapUpdateMultiplier, orderId },
        onUpdate,
        setIsLodaing
      )
    );
  };

  useEffect(() => {
    if (priceReview) {
      priceReview.multipliers?.current.forEach((current, index) => {
        methods.setValue(`multiplier.${index}`, {
          current: current.value.toFixed(3),
          estimator: priceReview.multipliers.estimator
            .find((item) => item.productLine === current.productLine)!
            .value.toFixed(3),
          name: priceReview.multipliers.estimator.find(
            (item) => item.productLine === current.productLine
          )!.productLine,
        });
      });
    }
  }, [priceReview, canEdit]);

  return (
    productLines && (
      <FormProvider {...methods}>
        <Container
          flex
          wHalf
          column
          backgroundColor={getColorsByStatus.backgroundColor}
          borderColor={getColorsByStatus.borderColor}
        >
          <H4>MULTIPLIER</H4>
          <Spacer h="8px" />
          <P color={silverChaliceTwo} fontSize={12}>
            The ‘Estimator’ field is for estimate purposes only. Net$ will use
            the ‘Estimator’ multiplier before submission. Net$ will use
            ‘Current’ multiplier after submission.
          </P>
          <Spacer h="16px" />
          <Wrapper flex wFull justifyEnd gap={45}>
            <HeaderWraper flex between>
              <P bold fontSize={12}>
                CURRENT
              </P>
              <P bold fontSize={12}>
                ESTIMATOR
              </P>
            </HeaderWraper>
          </Wrapper>
          <Spacer h="8px" />

          <Wrapper flex column gap={8}>
            {productLines.map((productLine, index) => (
              <Fragment key={productLine.name}>
                <Wrapper flex wFull between middle>
                  <Wrapper flex>
                    <P fontSize={12} bold>
                      {productLine.name.toUpperCase()}
                    </P>
                  </Wrapper>
                  <Wrapper flex gap={41}>
                    <Wrapper flex column gap={8} widthPX={76} middle>
                      <StyledInput
                        disabled
                        value={productLine.current}
                        style={{ fontSize: '12px', fontWeight: 600 }}
                      />
                    </Wrapper>
                    <Wrapper flex column gap={8} widthPX={76} middle>
                      <Wrapper column flex>
                        <StyledInput
                          disabled={!isDisabled}
                          {...methods.register(
                            `multiplier.${productLine.index!}.estimator`,
                            onlyNumberWithThreeDecimalsValidation(
                              { required: true },
                              'Value'
                            )
                          )}
                          aria-invalid={isInputValid(productLine.index!)}
                          style={{ fontSize: '12px', fontWeight: 600 }}
                        />
                        <FormError
                          label="estimator"
                          error={
                            methods.formState.errors.multiplier
                              ? methods.formState.errors.multiplier[
                                  productLine.index!
                                ]?.estimator
                              : undefined
                          }
                          message="Invalid format"
                          validationSchema={onlyNumberWithThreeDecimalsValidation()}
                        />
                      </Wrapper>
                    </Wrapper>
                  </Wrapper>
                </Wrapper>
                {index !== productLines.length - 1 && <Divider />}
              </Fragment>
            ))}
          </Wrapper>
          <Spacer h="24px" />
          <Wrapper flex wFull justifyEnd gap={24}>
            <ButtonSecondary
              disabled={
                !isDisabled ||
                priceReview?.multipliers.current.every(
                  (item) =>
                    item.value ===
                    priceReview?.multipliers.estimator.find(
                      (estimator) => estimator.productLine === item.productLine
                    )?.value
                ) ||
                !!methods.formState.errors.multiplier?.length
              }
              onClick={methods.handleSubmit(() =>
                onSave({ ...methods.getValues(), resetToDefault: true })
              )}
            >
              Reset Multiplier to Default
            </ButtonSecondary>
            <ButtonPrimary
              disabled={
                !isDisabled ||
                multiplierWatcher.every(
                  (item) =>
                    Number(item.estimator) ===
                    Number(
                      priceReview?.multipliers.estimator.find(
                        (estimator) => estimator.productLine === item.name
                      )?.value
                    )
                ) ||
                !!methods.formState.errors.multiplier?.length
              }
              onClick={methods.handleSubmit(() =>
                onSave({ ...methods.getValues(), resetToDefault: false })
              )}
            >
              Save
            </ButtonPrimary>
          </Wrapper>
        </Container>
      </FormProvider>
    )
  );
};
